import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaRupeeSign, FaDollarSign } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import api from '../../utils/api';
import './UpiPayment.css';
import './PaymentTabs.css';

const PreBookingPayment = ({ slotId, onPaymentComplete, onCancel }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [slot, setSlot] = useState(null);
  const [activeTab, setActiveTab] = useState('upi'); // 'upi', 'zelle', or 'paypal'

  useEffect(() => {
    const fetchSlotDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch slot details
        const slotResponse = await api.get(`/api/slots/${slotId}`);
        setSlot(slotResponse.data);
        
        // Create a pre-booking payment
        const paymentResponse = await api.post('/api/payments/create-prebooking-payment', { 
          slotId,
          interviewType: slotResponse.data.interviewType
        });
        
        setPayment({
          _id: paymentResponse.data.paymentId,
          amount: paymentResponse.data.amount,
          currency: paymentResponse.data.currency,
          upiId: paymentResponse.data.upiId,
          zelleId: paymentResponse.data.zelleId,
          paypalEmail: paymentResponse.data.paypalEmail,
          qrCodeUrl: paymentResponse.data.qrCodeUrl,
          paymentMethod: paymentResponse.data.paymentMethod,
          status: 'pending'
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching slot details or creating payment:', err);
        setError(err.response?.data?.message || 'Failed to initialize payment');
        setLoading(false);
      }
    };

    if (slotId) {
      fetchSlotDetails();
    }
  }, [slotId]);

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshot(file);
      // Create a preview URL
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!transactionId.trim()) {
      setMessage({ text: 'Please enter the transaction ID', type: 'error' });
      return;
    }
    
    if (!screenshot) {
      setMessage({ text: 'Please upload a screenshot of the payment', type: 'error' });
      return;
    }
    
    setSubmitting(true);
    setMessage({ text: 'Uploading payment details... Please wait.', type: 'info' });
    
    try {
      // Create a new FormData instance
      const formData = new FormData();
      formData.append('paymentId', payment._id);
      formData.append('transactionId', transactionId);
      // Make sure the field name matches what the server expects
      formData.append('transactionScreenshot', screenshot);
      formData.append('slotId', slotId);
      
      // Log the form data for debugging (will be removed in production)
      console.log('Payment data being submitted:', {
        paymentId: payment._id,
        transactionId,
        slotId,
        screenshotName: screenshot.name,
        screenshotType: screenshot.type,
        screenshotSize: screenshot.size
      });
      
      // Submit payment proof with proper headers
      const paymentResponse = await api.post('/api/payments/submit-prebooking-payment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Payment submission successful:', paymentResponse.data);
      // No need to book the slot separately - the submitPreBookingPayment endpoint already handles this
      
      toast.success('Payment submitted and slot booked successfully! The interviewer will verify your payment.');
      
      // Notify parent component that payment is complete
      if (onPaymentComplete) {
        onPaymentComplete();
      } else {
        // Navigate to interviews page if no callback provided
        navigate('/interviews');
      }
      
    } catch (err) {
      console.error('Error submitting payment proof:', err);
      // More detailed error message
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Error submitting payment proof';
      
      setMessage({ 
        text: `Payment submission failed: ${errorMessage}`, 
        type: 'error' 
      });
      toast.error(`Payment submission failed: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayPalPayment = async () => {
    try {
      setSubmitting(true);
      setMessage({ text: 'Creating PayPal order...', type: 'info' });
      
      // Create PayPal order
      const orderResponse = await api.post('/api/payments/create-paypal-order', {
        paymentId: payment._id
      });
      
      // Redirect to PayPal for payment approval
      window.location.href = orderResponse.data.approvalUrl;
      
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      setMessage({ 
        text: `Failed to create PayPal order: ${error.response?.data?.message || error.message}`, 
        type: 'error' 
      });
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/slots/available');
    }
  };

  if (loading) {
    return <div className="upi-payment-loading">Loading payment details... Please wait.</div>;
  }

  if (error) {
    return (
      <div className="upi-payment-error">
        <p>{error}</p>
        <button className="btn-secondary" onClick={handleCancel}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="upi-payment-container">
      <h2>Payment Required Before Booking</h2>
      
      {slot && (
        <div className="slot-details">
          <h3>Slot Details</h3>
          <p><strong>Interview Type:</strong> {slot.interviewType}</p>
          <p><strong>Date:</strong> {new Date(slot.startTime).toLocaleString()}</p>
          <p><strong>Duration:</strong> {slot.interviewType === 'DSA' ? '40' : '50'} minutes</p>
          <p><strong>Interviewer:</strong> {slot.interviewer?.name}</p>
        </div>
      )}
      
      {payment && (
        <div className="payment-details">
          <h3>Payment Information</h3>
          <div className="payment-amount">
            <strong>Amount: </strong>
            <div className="price-row">
              {payment.currency === 'USD' ? (
                <>
                  <span className="price-amount">
                    <FaDollarSign className="currency-icon" />
                    {parseFloat(payment.amount).toFixed(2)}
                  </span>
                  <span className="price-converted">
                    (<FaRupeeSign className="currency-icon" />
                    {Math.ceil(parseFloat(payment.amount) * 83.5).toLocaleString()})
                  </span>
                </>
              ) : (
                <>
                  <span className="price-amount">
                    <FaRupeeSign className="currency-icon" />
                    {parseFloat(payment.amount).toFixed(2)}
                  </span>
                  <div className="split-payment-info">
                    <div><strong>S30 Balance Used:</strong> ₹{payment.usedS30Balance || 0}</div>
                    <div><strong>UPI/Zelle to Pay:</strong> ₹{payment.remainingAmount || payment.amount}</div>
                  </div>
                  <span className="price-converted">
                    (<FaDollarSign className="currency-icon" />
                    {Math.ceil(parseFloat(payment.amount) / 83.5 + 3)})
                  </span>
                </>
              )}
            </div>
          </div>
          
          {/* Payment Method Tabs */}
          <div className="payment-tabs">
            {payment.upiId && (
              <button 
                className={`tab-button ${activeTab === 'upi' ? 'active' : ''}`}
                onClick={() => setActiveTab('upi')}
              >
                UPI Payment
              </button>
            )}
            {payment.zelleId && (
              <button 
                className={`tab-button ${activeTab === 'zelle' ? 'active' : ''}`}
                onClick={() => setActiveTab('zelle')}
              >
                Zelle Payment
              </button>
            )}
            {payment.paypalEmail && (
              <button 
                className={`tab-button ${activeTab === 'paypal' ? 'active' : ''}`}
                onClick={() => setActiveTab('paypal')}
              >
                PayPal Payment
              </button>
            )}
          </div>
          
          <div className="tab-content">
            {activeTab === 'upi' && payment.upiId && (
              <div className="payment-method upi-method">
                <div className="payment-qr-container">
                  <p>Scan this QR code to pay via UPI:</p>
                  <div className="qr-code-wrapper">
                    {payment.qrCodeUrl ? (
                      <img 
                        src={payment.qrCodeUrl} 
                        alt="UPI QR Code" 
                        className="payment-qr-code"
                      />
                    ) : (
                      <div className="no-qr-message">
                        <p>No QR code available. Please use the UPI ID below.</p>
                      </div>
                    )}
                  </div>
                  <p className="payment-id">UPI ID: <strong>{payment.upiId}</strong></p>
                </div>
              </div>
            )}
            
            {activeTab === 'zelle' && payment.zelleId && (
              <div className="payment-method zelle-method">
                <div className="payment-instructions">
                  <h4>Zelle Payment Instructions</h4>
                  <ol>
                    <li>Open your bank's mobile app or website</li>
                    <li>Select "Send Money with Zelle®"</li>
                    <li>Enter the Zelle ID: <strong>{payment.zelleId}</strong></li>
                    <li>Enter the amount: <strong>${payment.currency === 'INR' ? Math.ceil(parseFloat(payment.amount) / 83.5) + 3 : Math.ceil(parseFloat(payment.amount)) + 3} USD</strong></li>
                    <li>Add a note with this transaction ID: <strong>{payment._id.toString().slice(-6)}</strong></li>
                    <li>Complete the payment in USD</li>
                  </ol>
                  <p className="note">
                    <strong>Note:</strong> Please include the transaction ID in the payment notes to help us verify your payment.
                  </p>
                </div>
              </div>
            )}
            
            {activeTab === 'paypal' && payment.paypalEmail && (
              <div className="payment-method paypal-method">
                <div className="payment-instructions">
                  <h4>PayPal Payment</h4>
                  <p>Click the button below to pay securely with PayPal:</p>
                  <div className="paypal-info">
                    <p><strong>Amount:</strong> ${payment.currency === 'INR' ? Math.ceil(parseFloat(payment.amount) / 83.5) : parseFloat(payment.amount)} USD</p>
                    <p><strong>Recipient:</strong> {payment.paypalEmail}</p>
                  </div>
                  <button 
                    className="btn-paypal"
                    onClick={() => handlePayPalPayment()}
                    disabled={submitting}
                  >
                    {submitting ? 'Processing...' : 'Pay with PayPal'}
                  </button>
                  <p className="note">
                    <strong>Note:</strong> You will be redirected to PayPal to complete your payment securely. After successful payment, your slot will be automatically booked.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {activeTab !== 'paypal' && (
            <form onSubmit={handleSubmit} className="payment-proof-form">
              <div className="form-group">
                <label htmlFor="transactionId">
                  {activeTab === 'upi' 
                    ? 'Transaction ID (Last 4 digits only):' 
                    : 'Transaction/Confirmation ID:'}
                </label>
                <input
                  type="text"
                  id="transactionId"
                  value={transactionId}
                  onChange={(e) => {
                    if (activeTab === 'upi') {
                      // Only allow up to 4 digits for UPI
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value.length <= 4) {
                        setTransactionId(value);
                      }
                    } else {
                      // Allow any characters for Zelle
                      setTransactionId(e.target.value);
                    }
                  }}
                  placeholder={activeTab === 'upi' 
                    ? 'Enter last 4 digits only' 
                    : 'Enter transaction/confirmation ID'}
                  maxLength={activeTab === 'upi' ? '4' : '50'}
                  pattern={activeTab === 'upi' ? '[0-9]{4}' : '.{1,50}'}
                  required
                />
                <small className="form-text">
                  {activeTab === 'upi' 
                    ? 'For security reasons, please enter only the last 4 digits of your UPI transaction ID'
                    : 'Please enter your Zelle transaction or confirmation ID'}
                </small>
              </div>
              
              <div className="form-group">
                <label htmlFor="screenshot">Payment Screenshot:</label>
                <input
                  type="file"
                  id="screenshot"
                  onChange={handleScreenshotChange}
                  accept="image/png, image/jpeg, image/jpg"
                  required
                />
                
                {previewUrl && (
                  <div className="screenshot-preview">
                    <img src={previewUrl} alt="Payment Screenshot Preview" />
                  </div>
                )}
              </div>
              
              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Payment & Book Slot'}
                </button>
              </div>
            </form>
          )}
          
          {activeTab === 'paypal' && message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PreBookingPayment;
